import { useReducer, useCallback, useMemo } from 'react';

const initial = {};

function fieldKey(fieldId) {
  return String(fieldId);
}

function reducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const { fieldId, fileList } = action.payload;
      const key = fieldKey(fieldId);
      const newEntries = Array.from(fileList).map((file) => ({ file, caption: '' }));
      return { ...state, [key]: [...(state[key] || []), ...newEntries] };
    }
    case 'REMOVE': {
      const { fieldId, index } = action.payload;
      const key = fieldKey(fieldId);
      const list = state[key] || [];
      return { ...state, [key]: list.filter((_, i) => i !== index) };
    }
    case 'SET_CAPTION': {
      const { fieldId, index, caption } = action.payload;
      const key = fieldKey(fieldId);
      const list = state[key] || [];
      return {
        ...state,
        [key]: list.map((e, i) => (i === index ? { ...e, caption } : e)),
      };
    }
    case 'RESET':
      return { ...initial };
    default:
      return state;
  }
}

/**
 * Per–form-field file lists (each entry { file, caption }) for wizard parity with ContributionComp.
 */
export default function useFilesByField() {
  const [buckets, dispatch] = useReducer(reducer, initial);

  const addFiles = useCallback((fieldId, fileList) => {
    if (!fileList || fileList.length === 0) return;
    dispatch({ type: 'ADD', payload: { fieldId, fileList } });
  }, []);

  const removeFile = useCallback((fieldId, index) => {
    dispatch({ type: 'REMOVE', payload: { fieldId, index } });
  }, []);

  const setCaption = useCallback((fieldId, index, caption) => {
    dispatch({ type: 'SET_CAPTION', payload: { fieldId, index, caption } });
  }, []);

  const resetFiles = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const totalCount = useMemo(
    () => Object.values(buckets).reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0),
    [buckets],
  );

  const allFilesFlat = useMemo(
    () =>
      Object.entries(buckets).flatMap(([fid, entries]) =>
        (entries || []).map((e) => ({
          ...e,
          fieldId: Number(fid),
        })),
      ),
    [buckets],
  );

  return { buckets, addFiles, removeFile, setCaption, resetFiles, totalCount, allFilesFlat };
}
